import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { useEffect, useState } from 'react';
import { LTButton, LTText } from '@lambdatestincprivate/lt-components';
import { CheckCircleFillIcon, FileZipIcon, LinkIcon, MarkGithubIcon } from '@primer/octicons-react';
import { closePack, usePackState } from '../../data/pack-store';
import { TrustFooter } from '../Landing/TrustFooter';
import styles from '../Landing/landing.module.css';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var STEPS = ['Opening evidence pack', 'Preparing your evidence…', 'Reading failure analysis'];

/**
 * A friendly, NON-SENSITIVE chip for the pack source. A URL pack's label is a
 * SAS-signed link (sig/se/st tokens) — never render it; show a neutral chip
 * instead. File names (the user's own pick) and the owner/repo of a GitHub
 * artifact are safe to show.
 */
function sourceChip(kind, label) {
  if (kind === 'file') {
    return {
      Icon: FileZipIcon,
      text: label || 'Evidence pack'
    };
  }
  if (kind === 'gh-artifact') {
    return {
      Icon: MarkGithubIcon,
      text: label || 'GitHub artifact'
    };
  }
  return {
    Icon: LinkIcon,
    text: 'Evidence pack'
  };
}

/**
 * The opening/unpack screen (store phase 'loading'). A single, no-scroll
 * viewport: the heading + progress card sit centered, the trust notes pin to
 * the bottom as a footer. The step checklist is cosmetic (the real unpack is
 * one fast step); as soon as the pack is ready the store flips to 'open'.
 */
export function OpeningPack() {
  var sourceLabel = usePackState(function (s) {
    return s.sourceLabel;
  });
  var sourceKind = usePackState(function (s) {
    return s.sourceKind;
  });
  // Step 0 lands done immediately; the active marker walks forward so the
  // list feels alive, then holds on the last step until the phase flips.
  var _useState = useState(1),
    _useState2 = _slicedToArray(_useState, 2),
    active = _useState2[0],
    setActive = _useState2[1];
  useEffect(function () {
    if (active >= STEPS.length - 1) return undefined;
    var t = setTimeout(function () {
      return setActive(function (a) {
        return Math.min(a + 1, STEPS.length - 1);
      });
    }, 900);
    return function () {
      return clearTimeout(t);
    };
  }, [active]);
  var _sourceChip = sourceChip(sourceKind, sourceLabel),
    ChipIcon = _sourceChip.Icon,
    chipText = _sourceChip.text;
  return /*#__PURE__*/_jsxs("div", {
    className: styles.openingWrap,
    children: [/*#__PURE__*/_jsxs("div", {
      className: styles.openingMain,
      children: [/*#__PURE__*/_jsxs("div", {
        className: styles.head,
        children: [/*#__PURE__*/_jsx(LTText, {
          as: "h1",
          text: "Open a test run\u2019s evidence",
          color: "var(--lt-text-primary)",
          className: styles.title,
          style: {
            fontSize: '28px',
            lineHeight: '36px',
            fontWeight: 600
          }
        }), /*#__PURE__*/_jsx(LTText, {
          as: "p",
          text: "Replay every step, screenshot, log and AI root cause from a Kane AI test run.",
          variant: LTText.TextVariant.BODY_REGULAR,
          color: "var(--lt-text-secondary)",
          className: styles.subtitle
        })]
      }), /*#__PURE__*/_jsxs("div", {
        className: styles.openingCard,
        children: [/*#__PURE__*/_jsxs("span", {
          className: styles.sourceChip,
          children: [/*#__PURE__*/_jsx("span", {
            className: styles.sourceChipIcon,
            "aria-hidden": "true",
            children: /*#__PURE__*/_jsx(ChipIcon, {
              size: 16
            })
          }), /*#__PURE__*/_jsx(LTText, {
            as: "span",
            text: chipText,
            variant: LTText.TextVariant.SMALL_BOLD,
            color: "var(--lt-text-primary)",
            className: styles.sourceChipLabel
          })]
        }), /*#__PURE__*/_jsx("div", {
          className: styles.stepList,
          role: "status",
          "aria-live": "polite",
          children: STEPS.map(function (label, i) {
            var done = i < active;
            var isActive = i === active;
            return /*#__PURE__*/_jsxs("span", {
              className: styles.step,
              children: [/*#__PURE__*/_jsxs("span", {
                className: styles.stepIcon,
                "aria-hidden": "true",
                children: [done && /*#__PURE__*/_jsx("span", {
                  className: styles.stepDone,
                  children: /*#__PURE__*/_jsx(CheckCircleFillIcon, {
                    size: 16
                  })
                }), isActive && /*#__PURE__*/_jsx("span", {
                  className: styles.spinner
                }), !done && !isActive && /*#__PURE__*/_jsx("span", {
                  className: styles.pendingCircle
                })]
              }), /*#__PURE__*/_jsx(LTText, {
                as: "span",
                text: label,
                variant: isActive ? LTText.TextVariant.SMALL_BOLD : LTText.TextVariant.SMALL_REGULAR,
                color: done || isActive ? 'var(--lt-text-primary)' : 'var(--lt-text-muted)'
              })]
            }, label);
          })
        }), /*#__PURE__*/_jsx(LTButton, {
          label: "Abort",
          variant: LTButton.Variant.DANGER,
          size: LTButton.Size.SMALL,
          onClick: function onClick() {
            return void closePack();
          }
        })]
      })]
    }), /*#__PURE__*/_jsx(TrustFooter, {})]
  });
}